import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "@/context/UserContext";
import "./user-avatar.scss";
import useRegisterCtaModal from "@/hooks/useRegisterCtaModal";
import useCustomLoginWithRedirect from "@/hooks/useCustomLoginWithRedirect";
import useLocalStorage from "@/hooks/useLocalStorage";
import { LIVE_VARIANT_VERSIONS } from "@/constants/live-variant-versions";

interface UserAvatarProps {
    onClickProfile: () => void;
}

/**
 * UserAvatar component displays user's avatar or login prompt.
 * @param {Object} props - Component props.
 * @param {Function} props.onClickProfile - Function to handle profile click.
 * @returns {JSX.Element} UserAvatar component.
 */
const UserAvatar: React.FC<UserAvatarProps> = ({ onClickProfile }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user } = useAuth0();
    const { userState } = useUser();
    const isUserAuthenticated = isAuthenticated || Boolean(userState?.is_created);
    const { handleRegister } = useRegisterCtaModal({ delay: undefined });
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const [preferredVersion, setPreferredVersion] = useLocalStorage<string | null>(
        "live-variant-preferred-version",
        null,
    );
    const liveVariantUrls = Object.values(LIVE_VARIANT_VERSIONS).reduce<string[]>((urls, version: any) => {
        if (!version.disabled) {
            urls.push(version.url);
        }

        return urls;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const pathVersion = location.pathname.split("/")[1];
    const version = pathVersion || "puzzle";
    const authReturnTo = useMemo(
        () => `${location.pathname}${location.search}${location.hash}`,
        [location.pathname, location.search, location.hash],
    );

    // Update local storage when URL path changes to a valid version
    useEffect(() => {
        const path = window.location.pathname;
        const matchedVersion = liveVariantUrls.find((version) => path.includes(version));
        if (matchedVersion && matchedVersion !== preferredVersion) {
            setPreferredVersion(matchedVersion);
        }
    }, [window.location.pathname, preferredVersion, setPreferredVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    // Get user initials for the avatar
    const getUserInitials = () => {
        if (userState?.first_name && userState?.last_name) {
            return `${userState.first_name[0]}${userState.last_name[0]}`.toUpperCase();
        }
        if (userState?.nickname) {
            return userState.nickname.slice(0, 2).toUpperCase();
        }
        return "";
    };

    const userInitials = isUserAuthenticated ? getUserInitials() : "";

    /**
     * Handles click on user profile section.
     */
    const handleUserProfileClicked = () => {
        if (isUserAuthenticated) {
            if (version === "social") {
                navigate(`/social/profile/${userState?.id}`);
            } else if (userState?.nickname) {
                const encodedNickname = encodeURIComponent(userState.nickname.trim());
                navigate(`/profile/${encodedNickname}`);
            } else {
                // Keep legacy drawer fallback only when nickname is unavailable.
                onClickProfile();
            }
        } else {
            handleRegister(() =>
                customLoginWithRedirect({
                    authorizationParams: {
                        // Must match Auth0Provider default — /puzzle now redirects to / and drops OAuth params.
                        redirect_uri: window.location.origin,
                    },
                    appState: {
                        version,
                        location: "Header",
                        returnTo: authReturnTo,
                    },
                }),
            );
        }
    };

    /**
     * Render the avatar content based on the authentication status.
     */
    const renderAvatarContent = () => {
        if (isUserAuthenticated) {
            if (userState?.profilePhotoUrl) {
                return <img className="user-profile" src={userState.profilePhotoUrl} alt="user-profile" />;
            } else if (userState?.useEmailAccountPicture && user?.picture) {
                return <img className="user-profile" src={user?.picture} alt="user-profile" />;
            } else {
                return (
                    <div className="user-profile default-avatar">
                        <span>{userInitials}</span>
                    </div>
                );
            }
        }

        return <span>Log in</span>;
    };

    return (
        <div className="user-login" onClick={handleUserProfileClicked}>
            {renderAvatarContent()}
        </div>
    );
};

export default UserAvatar;
